import { BoundSelectField, BoundSelectFieldProps, HasIdAndName, Optional, Value } from "@homebound/beam";
import { Observer } from "mobx-react";
import { ObjectState } from "src/utils/formState";
import { AiFieldProposalFormValue } from "./utils/aiFieldProposalForm";
import { AiProposalParse, parseAiString, pendingProposedValue, resolveAiProposalStatus } from "./utils/bindAiProposal";

/** Like Beam's `{ id, name }` overload of `BoundSelectField`, so callers can skip `getOptionValue`/`getOptionLabel`. */
export type BoundAiSelectFieldProps<O extends HasIdAndName<V>, V extends Value> = Optional<
  Omit<BoundSelectFieldProps<O, V>, "proposedValue">,
  "getOptionLabel" | "getOptionValue"
> & { aiProposal?: ObjectState<AiFieldProposalFormValue> };

export function BoundAiSelectField<O extends HasIdAndName<V>, V extends Value>(props: BoundAiSelectFieldProps<O, V>) {
  const { field, aiProposal, onSelect, ...others } = props;
  // GraphQL stores enum codes as strings; the field is a typed enum union.
  const parse = parseAiString as AiProposalParse<V>;
  // Observer wrapper needed for `aiProposal.status` / `fieldValue` so the pending proposal stays in sync.
  return (
    <Observer>
      {() => (
        <BoundSelectField
          field={field}
          proposedValue={pendingProposedValue(aiProposal, parse)}
          onSelect={(value, opt) => {
            field.set(value);
            resolveAiProposalStatus(aiProposal, parse, value);
            onSelect?.(value, opt);
          }}
          {...others}
        />
      )}
    </Observer>
  );
}
